<template>
  <ns-popup
    ref="popup"
    class="ns-opt-in"
    :closable="true"
    :greeting="headName ? translate('signupGreeting', { name: headName }) : ''"
    :head-logo="'/content/dam/apps/loyalty/' + language + '_' + market + '/enJoy_nuskinrewards_white.svg'"
    @close="close"
  >
    <template #pageIntroduction>
      <div class="c-body">
        <h2 class="c-heading title-small-bold">{{ translate('signupTitle') }}</h2>
        <p class="c-description p-medium tx-em-bold-800 tx-em-style-none">
          <span v-html="translate('signupText')"></span>
        </p>
        <ns-checkbox class="c-accept-checkbox" box-color="#6236FF" v-model="agreeTermsAndConditions">
          <template #label>
            <div class="input-text title-small-bold" v-html="translate('signupTermsAndConditions')" @click="openTermsAndConditions($event)"></div>
          </template>
        </ns-checkbox>
        <div class="c-button-container">
          <button class="primary-solid fluid short bc-accept title-small-bold" :disabled="!agreeTermsAndConditions" @click="optIn()">
            <span>{{ translate('signupJoinNow') }}</span>
          </button>
          <button class="primary-ghost fluid short bc-cancel title-small-bold" @click="close('loyaltyModalCancel')">
            <span>{{ translate('signupCancel') }}</span>
          </button>
        </div>
      </div>
    </template>
  </ns-popup>
</template>

<script>

import { UserService } from '@nuskin/ns-account'
import webLoyalty from '@nuskin/ns-loyalty-web'
import { ConfigService, RunConfigService, StringService, events, storage } from '@nuskin/ns-util'

import NsCheckbox from '../common/ns-checkbox.vue'
import NsPopup from '../common/ns-popup.vue'

import i18nSim from '../i18n-sim'

const CLOSE_MAPPING = {
  'background': events.loyalty.LOYALTY_MODAL_OUTSIDE_CANCEL,
  'closeButton': events.loyalty.LOYALTY_MODAL_XCANCEL,
  'loyaltyModalCancel': events.loyalty.LOYALTY_MODAL_CANCEL
}

export default {
  name: 'ns-loyalty-signup',
  components: {
    'ns-checkbox': NsCheckbox,
    NsPopup
  },
  props: {
    eventContext: {
      type: String,
      required: true
    },
    showOnMounted: {
      type: Boolean,
      default: false
    },
    target: {
      type: String,
      default: '_blank'
    }
  },
  data: function () {
    return {
      headLogo: '',
      headName: '',

      agreeTermsAndConditions: false,
      language: '',
      market: '',
      termsAndConditions: '#',
      content: {}
    }
  },
  methods: {
    translate (key, data) {
      return i18nSim.$t(this.content, key, data)
    },
    reset () {
      this.agreeTermsAndConditions = false

      this.$refs.popup.reset()
    },
    close (eventName) {
      if (eventName) {
        events.publish(CLOSE_MAPPING[eventName] || eventName, { context: this.eventContext })
      }

      this.$refs.popup.close()
    },
    open (force) {
      if (force) {
        this.reset()
        this._open_()
      } else {
        webLoyalty.loyaltyCanOptIn().then((canOptIn) => {
          if (canOptIn) {
            this.reset()
            this._open_()
          }
        })
      }
    },
    async _open_ () {
      if (!this.headName) {
        if (UserService.isLoggedIn()) {
          let user = await UserService.getUser()
          this.headName = user.firstName || ''
        }
      }

      this.$refs.popup.open()
    },
    optIn () {
      webLoyalty.loyaltyOptIn().then((response) => {
        events.publish(events.loyalty.LOYALTY_JOIN_NOW, { context: this.eventContext, success: !!response.success })

        if (response.success) {
          events.publish(events.loyalty.LOYALTY_SIGNUP_UPDATE)

          this.close()
        } else {
          this.$refs.popup.showFeedback(this.translate('signupOptInError'), response.message)
        }
      })
    },
    openTermsAndConditions () {
      events.publish(events.loyalty.LOYALTY_TERMS_AND_CONDITIONS, { context: this.eventContext })
      window.open(this.termsAndConditions, this.target)
    },
    publishEvent (eventName, payload, ev) {
      events.publish(eventName, { ...payload, context: this.eventContext })

      if (ev) {
        ev.preventDefault()
      }
    }
  },
  created () {
    const marketConfig = ConfigService.getMarketConfig()
    const runConfig = RunConfigService.getRunConfig()

    this.language = runConfig.language
    this.market = runConfig.country

    this.headLogo = marketConfig.loyalty.landingPageLogo || '/content/dam/apps/loyalty/global/enJoy_nuskinrewards_white.svg'

    this.termsAndConditions = `/content/dam/apps/loyalty/${this.language}_${this.market}/TermsAndConditions.pdf`

    StringService.getApplicationStrings('loyalty', {
      'signupGreeting': 'Hey <span class="tx-title-case">{name}</span>, are you ready to enjoy?',
      'signupTitle': 'Nu Skin enJoy Rewards',
      'signupText': 'EnJoy is a rewards based program based on how you spend. The more you shop, the more rewards you\'ll discover!',
      'signupTermsAndConditions': 'I have read, understand and agree to the Terms and Conditions.',
      'privacyNoticeLinkText': 'View Privacy Policy',
      'signupJoinNow': 'Join Now',
      'signupCancel': 'Cancel',
      'signupOptInError': 'Opt-in option is unavailable because an error occurred.'
    }).then((content) => {
      this.content = content
    })
  },
  destroyed () {
    events.unsubscribe(events.loyalty.LOYALTY_SIGNUP_OPEN)
  },
  beforeMount () {
    events.subscribe(events.loyalty.LOYALTY_SIGNUP_OPEN, (force) => {
      this.open(force)
    })
  },
  async mounted () {
    if (this.showOnMounted) {
      let user = await UserService.getUser()
      if (user) {
        let userId = user.id

        let userOptin = storage.getItem(storage.metadata.LOYALTY_SIGNUP_SHOW)
        if (userId !== userOptin) {
          storage.setItem(storage.metadata.LOYALTY_SIGNUP_SHOW, userId)

          this.open();
        }
      }
    }
  }
}

</script>

<style lang="scss" scoped>

@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

@import "../assets/css/text.mixins.scss";
@import "../assets/css/view-size.mixins";
@import "../assets/css/confirm-reject-button-container";

$color-ash: map.get(colors.$baseColors, ash);
$color-blackhole: map.get(colors.$baseColors, blackhole);
$color-text-link: #6236FF;
$color-black: #000000;
$color-white: #FFFFFF;

a {
  @include a-link( $color-text-link );
}

.tx-em-style-none {
  ::v-deep {
    b, em, i {
      font-style: normal;
    }
  }
}

.tx-em-bold-600 {
  ::v-deep {
    b, em, i {
      font-weight: 600;
    }
  }
}

.tx-em-bold-800 {
  ::v-deep {
    b, em, i {
      font-weight: 800;
    }
  }
}

.ns-opt-in {
  color: $color-blackhole;
  font-family: "proxima-nova";
}

.c-body {
  .c-description {
    margin: 16px 0 0;
  }

  .c-accept-checkbox {
    margin: 30px 0 0;

    .input-text {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.375em;
    }
  }

  .slot-container {
    overflow: hidden;
  }
}

::v-deep {
  a {
    @include a-link( $color-text-link );
  }

  .text-link {
    @include a-link( $color-text-link );
  }

  .tx-title-case {
    text-transform: capitalize;
  }
}

.c-heading {
  text-align: inherit;
  margin: 0;
  padding: 0;
  color: $color-blackhole;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3333em;
}

.c-button-container {
  @include confirm-reject-button-container();

  margin: 30px 0 0;
}

</style>
